/**
 * NIP-51 list type definitions.
 * Each entry describes a standard list or set kind, what tag items it expects,
 * and how to display it in the UI.
 */
import { nip19 } from 'nostr-tools';

export type TagType = 'r' | 'p' | 'e' | 'a' | 't' | 'relay' | 'word';

export interface TagTypeConfig {
  tag: TagType;
  label: string;
  placeholder: string;
  /** If true, a second "label" field is shown alongside the value */
  hasLabel?: boolean;
  /** Short description shown in the UI */
  hint: string;
}

export const TAG_TYPE_CONFIGS: Record<TagType, TagTypeConfig> = {
  r: {
    tag: 'r',
    label: 'URL',
    placeholder: 'https://example.com',
    hasLabel: true,
    hint: 'External URL',
  },
  p: {
    tag: 'p',
    label: 'Pubkey / npub',
    placeholder: 'npub1... or hex pubkey',
    hint: 'Nostr public key',
  },
  e: {
    tag: 'e',
    label: 'Event ID / note',
    placeholder: 'note1... or hex event id',
    hint: 'Nostr event reference',
  },
  a: {
    tag: 'a',
    label: 'Address (kind:pubkey:d-tag)',
    placeholder: '30023:pubkey:identifier or naddr1...',
    hint: 'Addressable event reference',
  },
  t: {
    tag: 't',
    label: 'Hashtag',
    placeholder: 'bitcoin',
    hint: 'Topic hashtag (no #)',
  },
  relay: {
    tag: 'relay',
    label: 'Relay URL',
    placeholder: 'wss://relay.example.com',
    hint: 'Nostr relay WebSocket URL',
  },
  word: {
    tag: 'word',
    label: 'Word / phrase',
    placeholder: 'spamword',
    hint: 'Lowercase word to mute',
  },
};

export interface NIP51ListKind {
  kind: number;
  name: string;
  description: string;
  /** Whether users can have multiple lists (sets) vs one (standard) */
  isSet: boolean;
  /** The tag types this list kind supports as items */
  tagTypes: TagType[];
  icon: string;
}

/** All supported NIP-51 list kinds, plus the Nostree link list (30003). */
export const NIP51_KINDS: NIP51ListKind[] = [
  {
    kind: 30003,
    name: 'Link List',
    description: 'A curated list of external links (Nostree format)',
    isSet: true,
    tagTypes: ['r'],
    icon: '🔗',
  },
  {
    kind: 10003,
    name: 'Bookmarks',
    description: 'Uncategorized global list of things to save',
    isSet: false,
    tagTypes: ['e', 'a', 'r', 't'],
    icon: '🔖',
  },
  {
    kind: 30004,
    name: 'Article Curation Set',
    description: 'Articles and notes grouped by topic',
    isSet: true,
    tagTypes: ['a', 'e'],
    icon: '📰',
  },
  {
    kind: 30005,
    name: 'Video Curation Set',
    description: 'Videos grouped by topic or interest',
    isSet: true,
    tagTypes: ['e'],
    icon: '🎬',
  },
  {
    kind: 30000,
    name: 'Follow Set',
    description: 'A categorized group of people to follow',
    isSet: true,
    tagTypes: ['p'],
    icon: '👥',
  },
  {
    kind: 10000,
    name: 'Mute List',
    description: 'People, hashtags, and words to mute from your feeds',
    isSet: false,
    tagTypes: ['p', 't', 'word', 'e'],
    icon: '🔇',
  },
  {
    kind: 10001,
    name: 'Pinned Notes',
    description: 'Notes you want to showcase on your profile',
    isSet: false,
    tagTypes: ['e'],
    icon: '📌',
  },
  {
    kind: 30015,
    name: 'Interest Set',
    description: 'A named set of hashtag topics',
    isSet: true,
    tagTypes: ['t'],
    icon: '💡',
  },
  {
    kind: 30002,
    name: 'Relay Set',
    description: 'A named group of relays',
    isSet: true,
    tagTypes: ['relay'],
    icon: '📡',
  },
  {
    kind: 10006,
    name: 'Blocked Relays',
    description: 'Relays clients should never connect to',
    isSet: false,
    tagTypes: ['relay'],
    icon: '🚫',
  },
  {
    kind: 10007,
    name: 'Search Relays',
    description: 'Relays to use for search queries',
    isSet: false,
    tagTypes: ['relay'],
    icon: '🔍',
  },
];

/** The full set of kinds we support reading/displaying */
export const SUPPORTED_KINDS = NIP51_KINDS.map((k) => k.kind);

export function getKindConfig(kind: number): NIP51ListKind | undefined {
  return NIP51_KINDS.find((k) => k.kind === kind);
}

/** Standard (non-set) list kinds have only one list per user — no d-tag needed for lookup */
export const STANDARD_LIST_KINDS = NIP51_KINDS.filter((k) => !k.isSet).map((k) => k.kind);

/** Set list kinds use d-tags and can have multiple per user */
export const SET_LIST_KINDS = NIP51_KINDS.filter((k) => k.isSet).map((k) => k.kind);

/**
 * Decode an npub/note/naddr/nprofile bech32 value into the raw hex/coordinate
 * string that goes into the Nostr event tag.
 */
/**
 * Decode an npub/note/naddr/nprofile bech32 value into the raw hex/coordinate
 * string that goes into the Nostr event tag.
 */
export function resolveTagValue(raw: string): string {
  const trimmed = raw.trim();
  try {
    if (trimmed.startsWith('npub1')) {
      const { data } = nip19.decode(trimmed);
      return data as string;
    }
    if (trimmed.startsWith('note1')) {
      const { data } = nip19.decode(trimmed);
      return data as string;
    }
    if (trimmed.startsWith('naddr1')) {
      const decoded = nip19.decode(trimmed);
      const data = decoded.data as { kind: number; pubkey: string; identifier: string };
      return `${data.kind}:${data.pubkey}:${data.identifier}`;
    }
    if (trimmed.startsWith('nprofile1')) {
      const decoded = nip19.decode(trimmed);
      const data = decoded.data as { pubkey: string };
      return data.pubkey;
    }
  } catch {
    // fall through
  }
  return trimmed;
}
